/** Objectives — create form (RHF + zod + shadcn Form via Refine useForm). */
import { useForm } from "@refinedev/react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";

import {
  createObjectiveSchema,
  ENTITY_STATUS,
  type CreateObjectiveInput,
} from "@aos/shared";
import { Form } from "@/components/ui/form";
import { Button } from "@/components/ui/button";
import {
  Card,
  CardContent,
  CardFooter,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";

import { PageHeader } from "../projects/_shared";
import {
  FormGrid,
  MarkdownField,
  SelectField,
  TextField,
} from "../projects/_form";

/**
 * ObjectiveCreate (D6) — create form backed by `createObjectiveSchema`. The
 * description is markdown (rendered via the shared editor); status is a Select;
 * project / owner ids are plain fields. Submits via Refine useForm
 * (POST /api/objectives) and redirects to the list.
 */
export function ObjectiveCreate() {
  const form = useForm<CreateObjectiveInput, any, CreateObjectiveInput>({
    resolver: zodResolver(createObjectiveSchema),
    refineCoreProps: { resource: "objectives", action: "create" },
    defaultValues: { status: "active" },
  });

  const {
    refineCore: { formLoading },
    saveButtonProps,
    control,
  } = form;

  return (
    <div className="mx-auto max-w-3xl space-y-6">
      <PageHeader
        title="New Objective"
        description="Define an outcome and the period it should be achieved in."
      />
      <Form {...form}>
        <form onSubmit={saveButtonProps.onClick}>
          <Card>
            <CardHeader>
              <CardTitle className="text-base">Objective details</CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <TextField
                control={control}
                name="title"
                label="Title"
                placeholder="Grow qualified pipeline by 30%"
              />
              <MarkdownField
                control={control}
                name="description"
                label="Description"
                placeholder="What outcome are we steering toward, and why?"
              />
              <FormGrid>
                <TextField
                  control={control}
                  name="period"
                  label="Period"
                  placeholder="2026-Q3"
                />
                <SelectField
                  control={control}
                  name="status"
                  label="Status"
                  options={ENTITY_STATUS}
                />
                <TextField
                  control={control}
                  name="project_id"
                  label="Project ID"
                  placeholder="prj_…"
                />
                <TextField
                  control={control}
                  name="owner_user_id"
                  label="Owner user ID"
                  placeholder="user_…"
                />
              </FormGrid>
            </CardContent>
            <CardFooter className="justify-end">
              <Button type="submit" disabled={formLoading}>
                {formLoading ? "Creating…" : "Create Objective"}
              </Button>
            </CardFooter>
          </Card>
        </form>
      </Form>
    </div>
  );
}

export default ObjectiveCreate;
